import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';

import MainButton from '@/components/UI/mobile/MainButton';
import { getUploadFilePath } from '@/helpers/getUploadFilePath';
import { getAxiosWithAuth } from '@/lib/axios';
import { Feedback } from '@/types/database/feedback';

import CreateFeedbackFormItem from '../CreateFeedbackFormItem';
import styles from './index.module.scss';
import type { FeedbackForm } from './types';
import { feedbackFormSchema } from './types';

const CreateFeedbackForm: FC<{
  user: { id: string; name: string };
  houseNumber: string;
  onClose: () => void;
  feedbackTypes: string[];
}> = ({ user, houseNumber, onClose, feedbackTypes }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<FeedbackForm>({
    defaultValues: { houseNumber, type: feedbackTypes[0] },
    resolver: zodResolver(feedbackFormSchema),
  });

  const content = watch('content');

  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const { ref: fileRegisterRef, ...restFileRegister } = register('files');
  const image = watch('files')?.[0];
  const imageUrl = image && URL.createObjectURL(image);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      const newFeedback: Omit<
        Feedback,
        'createdTime' | 'updatedTime' | 'status' | '_id'
      > = {
        type: data.type,
        householdNumber: data.houseNumber,
        user,
        title: data.title,
        content: data.content,
        image: null,
      };
      if (data?.files?.[0]) {
        const file = data.files[0];
        const filePath = await getUploadFilePath(file, 'feedback');
        newFeedback.image = { name: file.name, path: filePath };
      }
      const axios = getAxiosWithAuth();
      await axios.post('/ezcon/api/feedbacks', newFeedback);
      handleClose();
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: '發生錯誤',
        text: '請稍後再試',
        confirmButtonColor: '#69b49e',
        icon: 'error',
      });
    }
  });

  return (
    <form className={styles.createFeedbackForm} onSubmit={onSubmit}>
      <CreateFeedbackFormItem label="房號">
        <input
          type="text"
          className={styles.houseNumber}
          {...register('houseNumber')}
          disabled
        />
      </CreateFeedbackFormItem>
      <CreateFeedbackFormItem
        label="類別"
        labelFor="type"
        error={errors.type?.message}
        required
      >
        <div className={styles.typeWrapper}>
          <select
            className={styles.type}
            {...register('type', { required: true })}
            style={{
              borderColor: errors.type ? '#ff0000' : undefined,
              outlineColor: errors.type ? '#ff0000' : undefined,
            }}
          >
            {feedbackTypes?.map((option: string) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </CreateFeedbackFormItem>
      <CreateFeedbackFormItem
        label="標題"
        labelFor="title"
        error={errors.title?.message}
        required
      >
        <input
          type="text"
          className={styles.title}
          {...register('title', { required: true })}
          style={{
            borderColor: errors.title ? '#ff0000' : undefined,
            outlineColor: errors.title ? '#ff0000' : undefined,
          }}
        />
      </CreateFeedbackFormItem>
      <CreateFeedbackFormItem
        label="內容"
        labelFor="description"
        error={errors.content?.message}
        required
      >
        <div className={styles.descriptionWrapper}>
          <textarea
            className={styles.description}
            {...register('content', { required: true, maxLength: 100 })}
          />
          <span className={styles.descriptionLength}>
            {content?.length ?? 0}/100
          </span>
        </div>
      </CreateFeedbackFormItem>
      <CreateFeedbackFormItem label="附件" labelFor="file">
        <input
          id="file"
          className={styles.file}
          type="file"
          accept="image/*"
          {...restFileRegister}
          multiple={false}
          hidden
          ref={(e) => {
            fileRegisterRef(e);
            uploadInputRef.current = e;
          }}
        />
        <button
          type="button"
          className={styles.uploadButton}
          onClick={() => uploadInputRef.current?.click()}
        >
          {imageUrl && <img src={imageUrl} alt="upload image" />}
          <span>上傳檔案</span>
        </button>
      </CreateFeedbackFormItem>
      <div className={styles.buttonBox}>
        <MainButton onClick={handleClose}>取消</MainButton>
        <MainButton type="submit">送出</MainButton>
      </div>
    </form>
  );
};

export default CreateFeedbackForm;
